import { Box, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import type { ReferenceRow, FieldKey } from "./qualificationLinkState";
import { displayLinkValue, FIELD_LABEL_ID } from "./wizardTypes";

// ponytail: rail blues are hardcoded, not theme tokens (KAdmin primary is green).
const styles = {
  rail: {
    width: { xs: "auto", sm: 260 },
    maxHeight: { xs: 160, sm: "none" },
    flexShrink: 0,
    borderRight: { xs: 0, sm: 1 },
    borderBottom: { xs: 1, sm: 0 },
    borderColor: "divider",
    overflowY: "auto",
    bgcolor: "#f4f6f8",
  },
  header: { px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", bgcolor: "#e3ebf3" },
  card: { px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" },
  fieldLabel: { lineHeight: 1.2, display: "block" },
} as const;

type EntryRailProps = {
  entries: ReferenceRow[];
};

// Step 2 left rail: read-only, one card per entry (active link). Scrolls internally so
// the dialog stays height-bounded even with many entries.
export default function EntryRail({ entries }: EntryRailProps) {
  const { formatMessage: t } = useIntl();

  // Hidden for a single Licensure Body: nothing to scope between.
  if (new Set(entries.map((entry) => entry.licensureBodyId)).size < 2) return null;

  return (
    <Box sx={styles.rail}>
      <Box sx={styles.header}>
        <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
          {t({ id: "allLabel" })}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t({ id: "skuQualificationEntryCount" }, { count: entries.length })}
        </Typography>
      </Box>
      {entries.map((entry, i) => (
        <Box key={i} sx={styles.card}>
          {(["lb", "ab", "sa"] as FieldKey[]).map((f) => (
            <Box key={f} sx={{ mb: 0.75 }}>
              <Typography variant="overline" color="text.secondary" sx={styles.fieldLabel}>
                {t({ id: FIELD_LABEL_ID[f] })}
              </Typography>
              <Typography variant="body2">{displayLinkValue(entry[f], t)}</Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
